import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  query,
  orderBy,
  Timestamp,
  arrayUnion,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import rentalQuestions from "../data/rental_questions.json";
import { generateContract } from "../utils/generateContract";
import { generateContractV2 } from "../utils/generateContractV2";

// --- Types ---

type SubmissionStatus = "new" | "pending" | "complete";

interface FieldEdit {
  field: string;
  originalValue: any;
  newValue: any;
  editedBy: string;
  editedAt: Timestamp;
  reason: string;
}

interface Submission {
  id: string;
  data: Record<string, any>;
  status: SubmissionStatus;
  submittedAt: Date | null;
  edits: FieldEdit[];
}

// --- Styled Components ---

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f0f2f5;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
`;

const Header = styled.header`
  background: linear-gradient(135deg, rgb(222, 110, 73), rgb(200, 90, 60));
  color: white;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const HeaderTitle = styled.h1`
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.9rem;
`;

const LogoutButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.4);
  padding: 0.4rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const DashboardLayout = styled.div`
  display: flex;
  height: calc(100vh - 60px);

  @media (max-width: 900px) {
    flex-direction: column;
    height: auto;
  }
`;

const Sidebar = styled.div`
  width: 420px;
  background: white;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 900px) {
    width: 100%;
    max-height: 50vh;
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
  }
`;

const FilterTabs = styled.div`
  display: flex;
  border-bottom: 2px solid #e9ecef;
  padding: 0 0.5rem;
  background: #fafafa;
  flex-shrink: 0;
`;

const FilterTab = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 0.75rem 0.5rem;
  border: none;
  background: none;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ active }) => (active ? "rgb(222, 110, 73)" : "#666")};
  border-bottom: 2px solid
    ${({ active }) => (active ? "rgb(222, 110, 73)" : "transparent")};
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: -2px;

  &:hover {
    color: rgb(222, 110, 73);
    background: rgba(222, 110, 73, 0.04);
  }
`;

const SubmissionList = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const SubmissionCard = styled.div<{ isSelected: boolean }>`
  padding: 1rem 1.2rem;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  background: ${({ isSelected }) =>
    isSelected ? "rgba(222, 110, 73, 0.06)" : "white"};
  border-left: 3px solid
    ${({ isSelected }) => (isSelected ? "rgb(222, 110, 73)" : "transparent")};
  transition: all 0.15s;

  &:hover {
    background: ${({ isSelected }) =>
      isSelected ? "rgba(222, 110, 73, 0.08)" : "#fafafa"};
  }
`;

const CardTitle = styled.div`
  font-weight: 600;
  color: #1a1a1a;
  font-size: 0.95rem;
  margin-bottom: 0.3rem;
`;

const CardMeta = styled.div`
  font-size: 0.8rem;
  color: #888;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-wrap: wrap;
`;

const StatusBadge = styled.span<{ status: SubmissionStatus }>`
  display: inline-block;
  padding: 0.15rem 0.6rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: ${({ status }) =>
    status === "new"
      ? "#e3f2fd"
      : status === "pending"
        ? "#fff3e0"
        : "#e8f5e9"};
  color: ${({ status }) =>
    status === "new"
      ? "#1565c0"
      : status === "pending"
        ? "#e65100"
        : "#2e7d32"};
`;

const DetailPanel = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  background: #f8f9fa;

  @media (max-width: 900px) {
    padding: 1.5rem;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #aaa;
  font-size: 1.1rem;
  gap: 0.5rem;
`;

const DetailCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const DetailHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const DetailTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
`;

const StatusSelect = styled.select`
  padding: 0.4rem 0.8rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  background: white;
  color: #333;

  &:focus {
    outline: none;
    border-color: rgb(222, 110, 73);
  }
`;

const DetailBody = styled.div`
  padding: 1.5rem 2rem;
`;

const SectionHeading = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: rgb(222, 110, 73);
  margin: 1.5rem 0 0.75rem 0;
  padding-bottom: 0.4rem;
  padding-top: 1rem;
  border-bottom: 2px solid rgba(222, 110, 73, 0.15);

  &:first-child {
    margin-top: 0;
  }
`;

const FieldRow = styled.div`
  display: flex;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f5f5f5;
  gap: 1rem;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0.2rem;
  }
`;

const FieldLabel = styled.span`
  font-weight: 600;
  color: #555;
  min-width: 200px;
  flex-shrink: 0;
  font-size: 0.9rem;

  @media (max-width: 600px) {
    min-width: auto;
    font-size: 0.85rem;
  }
`;

const FieldValue = styled.span`
  color: #1a1a1a;
  word-break: break-word;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const DayBlock = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin: 0.5rem 0;
  border-left: 3px solid rgb(222, 110, 73);
`;

const DayBlockTitle = styled.div`
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
`;

// Edit UI styles

const EditButton = styled.button`
  background: none;
  border: none;
  color: #aaa;
  cursor: pointer;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-size: 0.8rem;
  line-height: 1;
  flex-shrink: 0;
  transition: all 0.15s;

  &:hover {
    color: rgb(222, 110, 73);
    background: rgba(222, 110, 73, 0.08);
  }
`;

const EditForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
`;

const EditInput = styled.input`
  padding: 0.4rem 0.6rem;
  border: 2px solid rgb(222, 110, 73);
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: inherit;
  width: 100%;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(222, 110, 73, 0.12);
  }
`;

const EditTextarea = styled.textarea`
  padding: 0.4rem 0.6rem;
  border: 2px solid rgb(222, 110, 73);
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: inherit;
  width: 100%;
  min-height: 60px;
  resize: vertical;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(222, 110, 73, 0.12);
  }
`;

const EditSelect = styled.select`
  padding: 0.4rem 0.6rem;
  border: 2px solid rgb(222, 110, 73);
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: inherit;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(222, 110, 73, 0.12);
  }
`;

const ReasonInput = styled.input`
  padding: 0.3rem 0.6rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.8rem;
  font-family: inherit;
  color: #666;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #bbb;
  }
`;

const EditActions = styled.div`
  display: flex;
  gap: 0.4rem;
  align-items: center;
`;

const SaveButton = styled.button`
  background: rgb(222, 110, 73);
  color: white;
  border: none;
  padding: 0.3rem 0.8rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgb(200, 90, 60);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: #f0f0f0;
  color: #666;
  border: none;
  padding: 0.3rem 0.8rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #e0e0e0;
  }
`;

const EditedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: #fff3e0;
  color: #e65100;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.1rem 0.45rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  flex-shrink: 0;
  cursor: pointer;

  &:hover {
    background: #ffe0b2;
  }
`;

const EditHistoryPopover = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-top: 0.4rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  font-size: 0.8rem;
`;

const EditHistoryItem = styled.div`
  padding: 0.4rem 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
`;

const OriginalValue = styled.span`
  color: #999;
  text-decoration: line-through;
  font-size: 0.85rem;
`;

// Login page styles
const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0f2f5 0%, #e8e8e8 100%);
`;

const LoginCard = styled.div`
  background: white;
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  margin: 1rem;
`;

const LoginTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
  text-align: center;
`;

const LoginSubtitle = styled.p`
  color: #888;
  font-size: 0.9rem;
  text-align: center;
  margin: 0 0 2rem 0;
`;

const LoginInput = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  font-size: 1rem;
  margin-bottom: 1rem;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: rgb(222, 110, 73);
    box-shadow: 0 0 0 3px rgba(222, 110, 73, 0.1);
  }
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 0.8rem;
  background: linear-gradient(135deg, rgb(222, 110, 73), rgb(200, 90, 60));
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(222, 110, 73, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoginError = styled.div`
  background: #ffeaea;
  color: #d32f2f;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  margin-bottom: 1rem;
`;

const GenerateContractButton = styled.button`
  background: linear-gradient(135deg, #2e7d32, #388e3c);
  color: white;
  border: none;
  padding: 0.45rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(46, 125, 50, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  font-size: 1.1rem;
  color: #888;
`;

const CountBadge = styled.span`
  background: rgba(222, 110, 73, 0.12);
  color: rgb(200, 90, 60);
  font-size: 0.75rem;
  padding: 0.1rem 0.45rem;
  border-radius: 10px;
  margin-left: 0.3rem;
  font-weight: 700;
`;

// --- Helpers ---

function formatTimestamp(date: Date | null): string {
  if (!date) return "Unknown";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Build a label-to-index map from the questions JSON for stable ordering
const questionOrder = new Map(
  rentalQuestions.map((q: { label: string }, i: number) => [q.label, i]),
);

// Map labels to their question config for field-type-aware editing
const questionConfigByLabel = new Map(
  rentalQuestions.map((q: any) => [q.label, q]),
);

// Resolve the current display value for a field, accounting for admin edits
function getDisplayValue(
  data: Record<string, any>,
  edits: FieldEdit[],
  fieldKey: string,
): { value: any; isEdited: boolean; lastEdit?: FieldEdit } {
  const fieldEdits = edits?.filter((e) => e.field === fieldKey);
  const lastEdit = fieldEdits?.length
    ? fieldEdits[fieldEdits.length - 1]
    : undefined;
  if (lastEdit) {
    return { value: lastEdit.newValue, isEdited: true, lastEdit };
  }
  return { value: data[fieldKey], isEdited: false };
}

// Categorize fields into sections for display
function categorizeFields(data: Record<string, any>) {
  const clientFields = [
    "Client/Organization Name",
    "Client Contact Name",
    "Role/Title",
    "Phone Number",
    "Email Address",
    "Billing Address",
    "Organization Type",
  ];

  const eventFields = [
    "Event Duration",
    "Event Date",
    "Event Start Date",
    "Event End Date",
    "Event Dates",
    "Event Type",
    "Selected Dates",
    "Event Schedule",
    "Short Event Title",
    "Brief Description",
    "Anticipated Attendance",
    "Multi-Day Configuration",
  ];

  const sections: { title: string; fields: [string, any][] }[] = [
    { title: "Client Information", fields: [] },
    { title: "Event Details", fields: [] },
    { title: "Rental & Services", fields: [] },
    { title: "Per-Day Schedules", fields: [] },
  ];

  const skipFields = ["submittedAt", "status", "edits"];

  Object.entries(data).forEach(([key, value]) => {
    if (skipFields.includes(key)) return;
    if (key.startsWith("\u{1F4C5}")) {
      // day-specific entries (📅)
      sections[3].fields.push([key, value]);
    } else if (clientFields.includes(key)) {
      sections[0].fields.push([key, value]);
    } else if (eventFields.includes(key)) {
      sections[1].fields.push([key, value]);
    } else {
      sections[2].fields.push([key, value]);
    }
  });

  // Sort fields within each section by their order in rental_questions.json
  for (const section of sections) {
    if (section.title === "Per-Day Schedules") {
      // Sort day entries by day number (e.g., "📅 Day 1 (...)" -> 1)
      section.fields.sort((a, b) => {
        const dayNumA = parseInt(a[0].match(/Day (\d+)/)?.[1] || "0", 10);
        const dayNumB = parseInt(b[0].match(/Day (\d+)/)?.[1] || "0", 10);
        return dayNumA - dayNumB;
      });
    } else {
      section.fields.sort((a, b) => {
        const orderA = questionOrder.get(a[0]) ?? Infinity;
        const orderB = questionOrder.get(b[0]) ?? Infinity;
        return orderA - orderB;
      });
    }
  }

  return sections.filter((s) => s.fields.length > 0);
}

function renderValue(value: any): React.ReactNode {
  if (value === null || value === undefined || value === "") {
    return <span style={{ color: "#ccc", fontStyle: "italic" }}>—</span>;
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  if (typeof value === "object") {
    // Per-day schedule object
    return (
      <DayBlock>
        {Object.entries(value).map(([k, v]) => (
          <FieldRow key={k} style={{ borderBottom: "1px solid #eee" }}>
            <FieldLabel style={{ minWidth: "140px" }}>{k}</FieldLabel>
            <FieldValue>{renderValue(v)}</FieldValue>
          </FieldRow>
        ))}
      </DayBlock>
    );
  }
  return String(value);
}

// --- Component ---

export default function RentalAdmin() {
  const { currentUser, loading: authLoading, login, logout } = useAuth();

  // Login form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  // Admin verification
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [checkingAdmin, setCheckingAdmin] = useState(false);

  // Dashboard state
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<"all" | SubmissionStatus>(
    "all",
  );
  const [loadingData, setLoadingData] = useState(false);

  // Inline editing state
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [editReason, setEditReason] = useState<string>("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [historyField, setHistoryField] = useState<string | null>(null);
  const [generatingContract, setGeneratingContract] = useState(false);

  // Check if current user is an admin
  useEffect(() => {
    if (!currentUser?.email) {
      setIsAdmin(null);
      return;
    }

    async function checkAdmin() {
      setCheckingAdmin(true);
      try {
        const adminDoc = await getDoc(doc(db, "admins", currentUser!.email!));
        setIsAdmin(adminDoc.exists());
      } catch (err) {
        console.error("Admin check failed:", err);
        setIsAdmin(false);
      }
      setCheckingAdmin(false);
    }

    checkAdmin();
  }, [currentUser]);

  const fetchSubmissions = async () => {
    setLoadingData(true);
    try {
      const q = query(
        collection(db, "rentalRequests"),
        orderBy("submittedAt", "desc"),
      );
      const snapshot = await getDocs(q);
      const subs: Submission[] = snapshot.docs.map((d) => {
        const data = d.data();
        const submittedAt =
          data.submittedAt instanceof Timestamp
            ? data.submittedAt.toDate()
            : null;
        return {
          id: d.id,
          data,
          status: (data.status as SubmissionStatus) || "new",
          submittedAt,
          edits: (data.edits as FieldEdit[]) || [],
        };
      });
      setSubmissions(subs);
    } catch (err) {
      console.error("Error fetching submissions:", err);
    }
    setLoadingData(false);
  };

  // Fetch submissions when admin is verified
  useEffect(() => {
    if (!isAdmin) return;
    fetchSubmissions();
  }, [isAdmin]);

  // Handlers
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      await login(email, password);
    } catch (err: any) {
      setLoginError(
        err.code === "auth/invalid-credential"
          ? "Invalid email or password."
          : "Login failed. Please try again.",
      );
    }
    setLoggingIn(false);
  };

  const handleStatusChange = async (
    submissionId: string,
    newStatus: SubmissionStatus,
  ) => {
    try {
      await updateDoc(doc(db, "rentalRequests", submissionId), {
        status: newStatus,
      });
      setSubmissions((prev) =>
        prev.map((s) =>
          s.id === submissionId ? { ...s, status: newStatus } : s,
        ),
      );
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  const handleLogout = async () => {
    await logout();
    setIsAdmin(null);
    setSubmissions([]);
    setSelectedId(null);
  };

  const startEditing = (fieldKey: string, currentValue: any) => {
    setEditingField(fieldKey);
    setEditValue(
      Array.isArray(currentValue)
        ? currentValue.join(", ")
        : String(currentValue ?? ""),
    );
    setEditReason("");
    setHistoryField(null);
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue("");
    setEditReason("");
  };

  const handleSaveEdit = async (
    submissionId: string,
    fieldKey: string,
    originalValue: any,
  ) => {
    if (editValue === String(originalValue ?? "")) {
      cancelEditing();
      return;
    }
    setSavingEdit(true);
    try {
      const edit: FieldEdit = {
        field: fieldKey,
        originalValue: originalValue ?? "",
        newValue: editValue,
        editedBy: currentUser!.email!,
        editedAt: Timestamp.now(),
        reason: editReason,
      };
      await updateDoc(doc(db, "rentalRequests", submissionId), {
        edits: arrayUnion(edit),
      });
      // Update local state
      setSubmissions((prev) =>
        prev.map((s) =>
          s.id === submissionId ? { ...s, edits: [...s.edits, edit] } : s,
        ),
      );
      cancelEditing();
    } catch (err) {
      console.error("Error saving edit:", err);
    }
    setSavingEdit(false);
  };

  const toggleHistory = (fieldKey: string) => {
    setHistoryField((prev) => (prev === fieldKey ? null : fieldKey));
  };

  const handleGenerateContract = async (submission: Submission) => {
    setGeneratingContract(true);
    try {
      await generateContractV2({
        data: submission.data,
        edits: submission.edits,
        submittedAt: submission.submittedAt,
      });
    } catch (err) {
      console.error("Error generating contract:", err);
    }
    setGeneratingContract(false);
  };

  // --- Render ---

  if (authLoading) {
    return <LoadingSpinner>Loading...</LoadingSpinner>;
  }

  // Not logged in → login form
  if (!currentUser) {
    return (
      <LoginContainer>
        <LoginCard>
          <LoginTitle>SJFM Admin</LoginTitle>
          <LoginSubtitle>Sign in to manage rental submissions</LoginSubtitle>
          <form onSubmit={handleLogin}>
            {loginError && <LoginError>{loginError}</LoginError>}
            <LoginInput
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
            <LoginInput
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <LoginButton type="submit" disabled={loggingIn}>
              {loggingIn ? "Signing in..." : "Sign In"}
            </LoginButton>
          </form>
        </LoginCard>
      </LoginContainer>
    );
  }

  // Checking admin status
  if (checkingAdmin) {
    return <LoadingSpinner>Verifying access...</LoadingSpinner>;
  }

  // Not an admin
  if (!isAdmin) {
    return (
      <LoginContainer>
        <LoginCard>
          <LoginTitle>Access Denied</LoginTitle>
          <LoginSubtitle>
            Your account ({currentUser.email}) does not have admin access.
          </LoginSubtitle>
          <LoginButton onClick={handleLogout}>Sign Out</LoginButton>
        </LoginCard>
      </LoginContainer>
    );
  }

  // Dashboard
  const filteredSubmissions =
    statusFilter === "all"
      ? submissions
      : submissions.filter((s) => s.status === statusFilter);

  const selected = submissions.find((s) => s.id === selectedId) || null;

  const counts = {
    all: submissions.length,
    new: submissions.filter((s) => s.status === "new").length,
    pending: submissions.filter((s) => s.status === "pending").length,
    complete: submissions.filter((s) => s.status === "complete").length,
  };

  return (
    <PageContainer>
      <Header>
        <HeaderTitle>SJFM Rental Admin</HeaderTitle>
        <HeaderRight>
          <span>{currentUser.email}</span>
          <LogoutButton onClick={fetchSubmissions} disabled={loadingData}>
            {loadingData ? "Refreshing..." : "Refresh"}
          </LogoutButton>
          <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
        </HeaderRight>
      </Header>

      <DashboardLayout>
        <Sidebar>
          <FilterTabs>
            {(["all", "new", "pending", "complete"] as const).map((tab) => (
              <FilterTab
                key={tab}
                active={statusFilter === tab}
                onClick={() => setStatusFilter(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                <CountBadge>{counts[tab]}</CountBadge>
              </FilterTab>
            ))}
          </FilterTabs>

          <SubmissionList>
            {loadingData ? (
              <EmptyState style={{ padding: "2rem" }}>
                Loading submissions...
              </EmptyState>
            ) : filteredSubmissions.length === 0 ? (
              <EmptyState style={{ padding: "2rem" }}>
                No {statusFilter === "all" ? "" : statusFilter} submissions
              </EmptyState>
            ) : (
              filteredSubmissions.map((sub) => (
                <SubmissionCard
                  key={sub.id}
                  isSelected={selectedId === sub.id}
                  onClick={() => {
                    setSelectedId(sub.id);
                    cancelEditing();
                    setHistoryField(null);
                  }}
                >
                  <CardTitle>
                    {sub.data["Client/Organization Name"] ||
                      sub.data["Client Contact Name"] ||
                      "Unnamed"}
                  </CardTitle>
                  <CardMeta>
                    <StatusBadge status={sub.status}>{sub.status}</StatusBadge>
                    <span>{formatTimestamp(sub.submittedAt)}</span>
                  </CardMeta>
                </SubmissionCard>
              ))
            )}
          </SubmissionList>
        </Sidebar>

        <DetailPanel>
          {!selected ? (
            <EmptyState>
              <span style={{ fontSize: "2rem" }}>&#128203;</span>
              Select a submission to view details
            </EmptyState>
          ) : (
            <DetailCard>
              <DetailHeader>
                <DetailTitle>
                  {selected.data["Client/Organization Name"] ||
                    selected.data["Client Contact Name"] ||
                    "Submission Details"}
                </DetailTitle>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <GenerateContractButton
                    onClick={() => handleGenerateContract(selected)}
                    disabled={generatingContract}
                  >
                    {generatingContract ? "Generating..." : "Generate Contract"}
                  </GenerateContractButton>
                  <span
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: "#666",
                    }}
                  >
                    Status:
                  </span>
                  <StatusSelect
                    value={selected.status}
                    onChange={(e) =>
                      handleStatusChange(
                        selected.id,
                        e.target.value as SubmissionStatus,
                      )
                    }
                  >
                    <option value="new">New</option>
                    <option value="pending">Pending</option>
                    <option value="complete">Complete</option>
                  </StatusSelect>
                </div>
              </DetailHeader>

              <DetailBody>
                <FieldRow>
                  <FieldLabel>Submitted</FieldLabel>
                  <FieldValue>
                    {formatTimestamp(selected.submittedAt)}
                  </FieldValue>
                </FieldRow>

                {categorizeFields(selected.data).map((section) => (
                  <div key={section.title}>
                    <SectionHeading>{section.title}</SectionHeading>
                    {section.fields.map(([key, value]) => {
                      // Day-specific entries - render each sub-field as editable
                      if (
                        key.startsWith("\u{1F4C5}") &&
                        typeof value === "object" &&
                        value !== null
                      ) {
                        return (
                          <div key={key}>
                            <DayBlockTitle>{key}</DayBlockTitle>
                            <DayBlock>
                              {Object.entries(value).map(
                                ([subKey, subValue]) => {
                                  const compoundKey = `${key}|${subKey}`;
                                  const {
                                    value: subDisplayValue,
                                    isEdited: subIsEdited,
                                  } = getDisplayValue(
                                    selected.data,
                                    selected.edits,
                                    compoundKey,
                                  );
                                  // If no edit exists for compound key, use original subValue
                                  const finalValue = subIsEdited
                                    ? subDisplayValue
                                    : subValue;
                                  const subFieldEdits = selected.edits?.filter(
                                    (e) => e.field === compoundKey,
                                  );
                                  const isSubEditing =
                                    editingField === compoundKey;

                                  if (isSubEditing) {
                                    return (
                                      <FieldRow
                                        key={subKey}
                                        style={{
                                          borderBottom: "1px solid #eee",
                                          alignItems: "flex-start",
                                        }}
                                      >
                                        <FieldLabel
                                          style={{ minWidth: "140px" }}
                                        >
                                          {subKey}
                                        </FieldLabel>
                                        <EditForm>
                                          <EditInput
                                            type="text"
                                            value={editValue}
                                            onChange={(e) =>
                                              setEditValue(e.target.value)
                                            }
                                            autoFocus
                                            onKeyDown={(e) => {
                                              if (e.key === "Escape")
                                                cancelEditing();
                                              if (e.key === "Enter")
                                                handleSaveEdit(
                                                  selected.id,
                                                  compoundKey,
                                                  finalValue,
                                                );
                                            }}
                                          />
                                          <ReasonInput
                                            placeholder="Reason for edit (optional)"
                                            value={editReason}
                                            onChange={(e) =>
                                              setEditReason(e.target.value)
                                            }
                                            onKeyDown={(e) => {
                                              if (e.key === "Escape")
                                                cancelEditing();
                                              if (e.key === "Enter")
                                                handleSaveEdit(
                                                  selected.id,
                                                  compoundKey,
                                                  finalValue,
                                                );
                                            }}
                                          />
                                          <EditActions>
                                            <SaveButton
                                              disabled={savingEdit}
                                              onClick={() =>
                                                handleSaveEdit(
                                                  selected.id,
                                                  compoundKey,
                                                  finalValue,
                                                )
                                              }
                                            >
                                              {savingEdit
                                                ? "Saving..."
                                                : "Save"}
                                            </SaveButton>
                                            <CancelButton
                                              onClick={cancelEditing}
                                            >
                                              Cancel
                                            </CancelButton>
                                          </EditActions>
                                        </EditForm>
                                      </FieldRow>
                                    );
                                  }

                                  return (
                                    <div key={subKey}>
                                      <FieldRow
                                        style={{
                                          borderBottom: "1px solid #eee",
                                          alignItems: "center",
                                        }}
                                      >
                                        <FieldLabel
                                          style={{ minWidth: "140px" }}
                                        >
                                          {subKey}
                                        </FieldLabel>
                                        <FieldValue style={{ flex: 1 }}>
                                          {renderValue(finalValue)}
                                        </FieldValue>
                                        {subIsEdited && (
                                          <EditedBadge
                                            onClick={() =>
                                              toggleHistory(compoundKey)
                                            }
                                          >
                                            edited
                                          </EditedBadge>
                                        )}
                                        <EditButton
                                          title="Edit this field"
                                          onClick={() =>
                                            startEditing(
                                              compoundKey,
                                              finalValue,
                                            )
                                          }
                                        >
                                          &#9998;
                                        </EditButton>
                                      </FieldRow>
                                      {historyField === compoundKey &&
                                        subFieldEdits.length > 0 && (
                                          <EditHistoryPopover>
                                            <div
                                              style={{
                                                fontWeight: 700,
                                                marginBottom: "0.4rem",
                                                color: "#333",
                                              }}
                                            >
                                              Edit History
                                            </div>
                                            {subFieldEdits.map((edit, i) => (
                                              <EditHistoryItem key={i}>
                                                <div>
                                                  <OriginalValue>
                                                    {String(
                                                      edit.originalValue || "—",
                                                    )}
                                                  </OriginalValue>
                                                  {" → "}
                                                  <strong>
                                                    {String(edit.newValue)}
                                                  </strong>
                                                </div>
                                                <div
                                                  style={{
                                                    color: "#888",
                                                    marginTop: "0.15rem",
                                                  }}
                                                >
                                                  {edit.editedBy} &middot;{" "}
                                                  {edit.editedAt instanceof
                                                  Timestamp
                                                    ? formatTimestamp(
                                                        edit.editedAt.toDate(),
                                                      )
                                                    : "Unknown time"}
                                                  {edit.reason && (
                                                    <>
                                                      {" "}
                                                      &middot;{" "}
                                                      <span
                                                        style={{
                                                          fontStyle: "italic",
                                                        }}
                                                      >
                                                        {edit.reason}
                                                      </span>
                                                    </>
                                                  )}
                                                </div>
                                              </EditHistoryItem>
                                            ))}
                                          </EditHistoryPopover>
                                        )}
                                    </div>
                                  );
                                },
                              )}
                            </DayBlock>
                          </div>
                        );
                      }

                      const { value: displayValue, isEdited } = getDisplayValue(
                        selected.data,
                        selected.edits,
                        key,
                      );
                      const fieldEdits = selected.edits?.filter(
                        (e) => e.field === key,
                      );
                      const qConfig = questionConfigByLabel.get(key);
                      const isCurrentlyEditing = editingField === key;

                      // Render inline edit form
                      if (isCurrentlyEditing) {
                        return (
                          <FieldRow
                            key={key}
                            style={{ alignItems: "flex-start" }}
                          >
                            <FieldLabel>{key}</FieldLabel>
                            <EditForm>
                              {qConfig?.type === "textarea" ? (
                                <EditTextarea
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  autoFocus
                                />
                              ) : qConfig?.type === "select" ? (
                                <EditSelect
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  autoFocus
                                >
                                  {qConfig.options?.map((opt: any) => (
                                    <option
                                      key={opt.value || opt.label}
                                      value={opt.label}
                                    >
                                      {opt.label}
                                    </option>
                                  ))}
                                </EditSelect>
                              ) : (
                                <EditInput
                                  type={
                                    qConfig?.type === "number"
                                      ? "number"
                                      : qConfig?.type === "email"
                                        ? "email"
                                        : qConfig?.type === "tel"
                                          ? "tel"
                                          : "text"
                                  }
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === "Escape") cancelEditing();
                                    if (e.key === "Enter")
                                      handleSaveEdit(
                                        selected.id,
                                        key,
                                        displayValue,
                                      );
                                  }}
                                />
                              )}
                              <ReasonInput
                                placeholder="Reason for edit (optional)"
                                value={editReason}
                                onChange={(e) => setEditReason(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Escape") cancelEditing();
                                  if (e.key === "Enter")
                                    handleSaveEdit(
                                      selected.id,
                                      key,
                                      displayValue,
                                    );
                                }}
                              />
                              <EditActions>
                                <SaveButton
                                  disabled={savingEdit}
                                  onClick={() =>
                                    handleSaveEdit(
                                      selected.id,
                                      key,
                                      displayValue,
                                    )
                                  }
                                >
                                  {savingEdit ? "Saving..." : "Save"}
                                </SaveButton>
                                <CancelButton onClick={cancelEditing}>
                                  Cancel
                                </CancelButton>
                              </EditActions>
                            </EditForm>
                          </FieldRow>
                        );
                      }

                      // Render display mode with edit button
                      return (
                        <div key={key}>
                          <FieldRow style={{ alignItems: "center" }}>
                            <FieldLabel>{key}</FieldLabel>
                            <FieldValue style={{ flex: 1 }}>
                              {renderValue(displayValue)}
                            </FieldValue>
                            {isEdited && (
                              <EditedBadge onClick={() => toggleHistory(key)}>
                                edited
                              </EditedBadge>
                            )}
                            {key !== "Event Duration" &&
                              key !== "Event Type" &&
                              key !== "Event Dates" && (
                                <EditButton
                                  title="Edit this field"
                                  onClick={() =>
                                    startEditing(key, displayValue)
                                  }
                                >
                                  &#9998;
                                </EditButton>
                              )}
                          </FieldRow>
                          {/* Edit history popover */}
                          {historyField === key && fieldEdits.length > 0 && (
                            <EditHistoryPopover>
                              <div
                                style={{
                                  fontWeight: 700,
                                  marginBottom: "0.4rem",
                                  color: "#333",
                                }}
                              >
                                Edit History
                              </div>
                              {fieldEdits.map((edit, i) => (
                                <EditHistoryItem key={i}>
                                  <div>
                                    <OriginalValue>
                                      {String(edit.originalValue || "—")}
                                    </OriginalValue>
                                    {" → "}
                                    <strong>{String(edit.newValue)}</strong>
                                  </div>
                                  <div
                                    style={{
                                      color: "#888",
                                      marginTop: "0.15rem",
                                    }}
                                  >
                                    {edit.editedBy} &middot;{" "}
                                    {edit.editedAt instanceof Timestamp
                                      ? formatTimestamp(edit.editedAt.toDate())
                                      : "Unknown time"}
                                    {edit.reason && (
                                      <>
                                        {" "}
                                        &middot;{" "}
                                        <span style={{ fontStyle: "italic" }}>
                                          {edit.reason}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </EditHistoryItem>
                              ))}
                            </EditHistoryPopover>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </DetailBody>
            </DetailCard>
          )}
        </DetailPanel>
      </DashboardLayout>
    </PageContainer>
  );
}
